import { Button, Flex, Spacing } from '@billy/design-system'
import { PropsWithChildren, ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useSegment } from '../../../../hooks'
import { useEInvoicingSetup } from '../../hooks/useEInvoicingSetup'
import { DocumentType } from '../../types/documentType'
import { EInvoicingDocumentUploadInfoBanner } from '../EInvoicingDocumentUploadInfoBanner'
import { EInvoicingGenericStep } from '../EInvoicingStep/elements/EInvoicingGenericStep'
import { EInvoicingProgressBar } from '../EInvoicingStep/elements/EInvoicingProgressBar'
import { EInvoicingStepContainer } from '../EInvoicingStep/elements/EInvoicingStepContainer'
import { FileDropZone } from './elements'
import { useEInvoicingGenericUpload } from './hooks'
import { getEventNameFromDocumentType } from './utils'

interface EInvoicingGenericUploadProps {
  documentType: DocumentType
  progressHeading: string
  subtitle?: string | ReactElement
  title: string
  uploadTitle: string
}

export const EInvoicingGenericUpload = ({
  documentType,
  progressHeading,
  subtitle,
  title,
  uploadTitle,
}: PropsWithChildren<EInvoicingGenericUploadProps>) => {
  const { t } = useTranslation()
  const { track } = useSegment()

  const {
    fileType,
    handleContinueToNextStep,
    handleDeleteButtonClick,
    onDropAccepted,
    isDocumentBeingCreated,
    isFileBeingUploaded,
    previewUrl,
  } = useEInvoicingGenericUpload(documentType)

  const { navigateToEInvoices } = useEInvoicingSetup()

  const handleContinue = useCallback(() => {
    const eventName = getEventNameFromDocumentType(documentType)
    if (eventName) {
      track(`xxx - e-invoicing: ${eventName} (Client)`)
    }
    handleContinueToNextStep()
  }, [documentType, handleContinueToNextStep, track])

  return (
    <EInvoicingStepContainer headerContent={<EInvoicingProgressBar title={progressHeading} />}>
      <EInvoicingGenericStep title={title}>
        <EInvoicingDocumentUploadInfoBanner />
        <FileDropZone
          fileUrl={previewUrl}
          fileType={fileType}
          isProcessing={isFileBeingUploaded}
          onDropAccepted={onDropAccepted}
          uploadTitle={uploadTitle}
          handleDeleteButtonClick={handleDeleteButtonClick}
        />
        <Flex flexDirection="column" gap={Spacing.S12}>
          <Button onClick={handleContinue} loading={isDocumentBeingCreated} disabled={!previewUrl} fullWidth>
            {t('e_invoice.kyc.step.upload.confirm')}
          </Button>
          <Button fullWidth variant="text" onClick={navigateToEInvoices}>
            {t('e_invoice.kyc.step.cancel')}
          </Button>
        </Flex>
      </EInvoicingGenericStep>
    </EInvoicingStepContainer>
  )
}
